import {appSessionStorage} from "../../services";

export class EnhancedEcommerce {
  protected static observableConfig: object = {
    'listSelector': 'solar-app',
    'itemsPerRowDesktop': 4,
    'itemsPerRowMobile': 2
  };

  protected static isMobile: boolean = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  protected static productList: Array<object> = [];

  addProductSearch(term: string) {
    window.dataLayer.push({
      'event': 'search',
      'search': {
        'search_term': term,
      },
      'user_data': EnhancedEcommerce.formatUser()
    });
  }

  addProductImpressions(products: Array<object>): void {
    const impressions: Array<object> = [];

    EnhancedEcommerce.productList = products;

    products.forEach((p, i) => {
      impressions.push({
        'name': p['name'],
        'id': p['sku'],
        'price': p['final_price_incl_tax'],
        'brand': p['brand'],
        'sku': p['sku'],
        'category': p.hasOwnProperty('category_names') ? p['category_names'].join(' / ') : '',
        'variant': '',
        'list': 'product-list',
        'position': (i + 1)
      });
    });

    window.dataLayer.push({
      'ecommerce': {
        'currencyCode': 'EUR',
        'impressions': impressions
      }
    });

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    }

    const observer = new IntersectionObserver(EnhancedEcommerce.addObservableProductImpressions, observerOptions);
    const rootElement = document.getElementById(EnhancedEcommerce.observableConfig['listSelector'] || 'impressions-observable');
    const itemsPerRow = EnhancedEcommerce.isMobile ? EnhancedEcommerce.observableConfig['itemsPerRowMobile'] : EnhancedEcommerce.observableConfig['itemsPerRowDesktop'];

    if (rootElement) {
      const observeTargets = rootElement.querySelectorAll('[data-index]');
      for (let i = 0; i < observeTargets.length; i++) {
        if (i % itemsPerRow === 0) {
          observer.observe(observeTargets[i]);
        }
      }
    }
  }

  static addObservableProductImpressions(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void {
    const productsClone = EnhancedEcommerce.productList;
    const itemsPerRow = EnhancedEcommerce.isMobile ? EnhancedEcommerce.observableConfig['itemsPerRowMobile'] : EnhancedEcommerce.observableConfig['itemsPerRowDesktop'];

    entries.map(entry => {
      if (entry.isIntersecting && entry.target.hasAttribute('data-index')) {
        const itemIndex = parseInt(entry.target.getAttribute('data-index'));
        EnhancedEcommerce.addObservableImpressions(productsClone.slice(itemIndex, itemIndex + itemsPerRow), itemIndex);

        observer.unobserve(entry.target);
      }
    });
  }

  static addObservableImpressions(products: Array<object>, index: number = 0): void {
    let items: Array<object> = [];

    products.forEach((product: object) => {
      index++;

      let productObj = EnhancedEcommerce.formatProduct(product);
      if (productObj) {
        productObj['index'] = index;

        items.push(productObj);
      }
    });

    window.dataLayer.push({
      'event': 'view_item_list',
      'ecommerce': {
        'currency': 'EUR',
        'item_list_name': '',
        'item_list_id': '',
        'items': items
      },
      'user_data': EnhancedEcommerce.formatUser()
    });
  }

  addProductClick(product: object, index: string | number | boolean | symbol): void {
    window.dataLayer.push({
      'event': 'productClick',
      'ecommerce': {
        'click': {
          'actionField': {'list': 'product-list'},
          'products': [{
            'name': product['name'],
            'id': product['sku'],
            'price': product['final_price_incl_tax'],
            'brand': product['brand'],
            'category': product.hasOwnProperty('category_names') ? product['category_names'].join(' / ') : '',
            'variant': '',
            'position': (index as number) + 1
          }]
        }
      }
    });
  }

  addProductAddToWishlist(product: object, qty: number = 1): void {
    window.dataLayer.push({
      'event': 'add_to_wishlist',
      'ecommerce': {
        'currency': 'EUR',
        'value': product['final_price_incl_tax'],
        'items': [EnhancedEcommerce.formatProduct(product, qty)]
      },
      'user_data': EnhancedEcommerce.formatUser()
    });
  }

  addProductDetail(product: object, qty: number = 1, user: object = null): void {
    window.dataLayer.push({
      'ecommerce': {
        'detail': {
          'products': [{
            'name': product['name'],
            'id': product['sku'],
            'price': product['final_price_incl_tax'],
            'brand': product['brand'],
            'category': product.hasOwnProperty('category_names') ? product['category_names'].join(' / ') : '',
            'variant': ''
          }]
        }
      }
    });

    window.dataLayer.push({
      'event': 'view_item',
      'ecommerce': {
        'currency': 'EUR',
        'value': product['final_price_incl_tax'],
        'items': [EnhancedEcommerce.formatProduct(product, qty)]
      },
      'user_data': EnhancedEcommerce.formatUser()
    });
  }

  addProductAddToCart(product: object, qty: number = 1): void {
    window.dataLayer.push({
      'event': 'addToCart',
      'ecommerce': {
        'currencyCode': 'EUR',
        'add': {
          'products': [{
            'name': product['name'],
            'id': product['sku'],
            'price': product['final_price_incl_tax'],
            'brand': product['brand'],
            'category': product.hasOwnProperty('category_names') ? product['category_names'].join(' / ') : '',
            'variant': '',
            'quantity': qty
          }]
        }
      }
    });

    window.dataLayer.push({
      'event': 'add_to_cart',
      'ecommerce': {
        'currency': 'EUR',
        'value': product['final_price_incl_tax'],
        'items': [EnhancedEcommerce.formatProduct(product, qty)]
      },
      'user_data': EnhancedEcommerce.formatUser()
    });
  }

  addProductRemoveFromCart(product: object): void {
    window.dataLayer.push({
      'event': 'removeFromCart',
      'ecommerce': {
        'remove': {
          'products': [{
            'name': product['name'],
            'id': product['sku'],
            'price': product['row_total_incl_tax'],
            'brand': '',
            'category': '',
            'variant': '',
            'quantity': product['qty']
          }]
        }
      }
    });
  }

  addCart(products: Array<object>, total: number, quoteIdentifier: string): void {
    const items: Array<object> = [];

    products.forEach((product, i) => {
      items.push(EnhancedEcommerce.formatProduct(product, product['qty'] || 1));
    });

    window.dataLayer.push({
      'event': 'view_cart',
      'ecommerce': {
        'currency': 'EUR',
        'value': total,
        'cart_id': 'cart-' + quoteIdentifier,
        'items': items
      },
      'user_data': EnhancedEcommerce.formatUser()
    });
  }

  addCheckoutPersonalData(products: Array<object>, total: number, step: number): void {
    const items: Array<object> = [];

    products.forEach((product, i) => {
      items.push(EnhancedEcommerce.formatProduct(product, product['qty'] || 1));
    });

    window.dataLayer.push({
      'event': 'begin_checkout',
      'ecommerce': {
        'currency': 'EUR',
        'value': total,
        'checkout_step': {
          step: step,
          step_name: "Persoonsgegevens"
        },
        'items': items
      },
      'user_data': EnhancedEcommerce.formatUser(true)
    });
  }

  addCheckoutShipping(products: Array<object>, total: number, step: number, shippingMethod: string, customAddressData: object = null): void {
    const items: Array<object> = [];

    products.forEach((product, i) => {
      items.push(EnhancedEcommerce.formatProduct(product, product['qty'] || 1));
    });

    window.dataLayer.push({
      'event': 'add_shipping_info',
      'ecommerce': {
        'currency': 'EUR',
        'value': total,
        'checkout_step': {
          step: step,
          step_name: "Verzendmethode"
        },
        shipping_tier: shippingMethod,
        'items': items
      },
      'user_data': EnhancedEcommerce.formatUser(true, true, false, customAddressData)
    });
  }

  addCheckoutPayment(products: Array<object>, total: number, step: number, paymentMethod: string, newsletter: boolean = false, customAddressData: object = null): void {
    const items: Array<object> = [];

    products.forEach((product, i) => {
      items.push(EnhancedEcommerce.formatProduct(product, product['qty'] || 1));
    });

    window.dataLayer.push({
      'event': 'add_payment_info',
      'ecommerce': {
        'currency': 'EUR',
        'value': total,
        'checkout_step': {
          step: step,
          step_name: "Betaalmethode"
        },
        'payment_type': paymentMethod,
        'newsletter_opt_in': newsletter,
        'items': items
      },
      'user_data': EnhancedEcommerce.formatUser(true, true, false, customAddressData)
    });
  }

  addCheckoutLogin(): void {

  }

  addCheckout(products: Array<object>, step: number, option: string = ''): void {
    const productList: Array<object> = [];

    if (step === 1) {
      products.forEach(p => {
        productList.push({
          'name': p['name'],
          'id': p['sku'],
          'price': p['row_total_incl_tax'],
          'brand': '',
          'category': '',
          'variant': '',
          'quantity': p['qty']
        });
      });

      window.dataLayer.push({
        'event': 'checkout',
        'ecommerce': {
          'checkout': {
            'actionField': {'step': step, 'option': option},
            'products': productList
          }
        }
      });
    } else {
      window.dataLayer.push({
        'event': 'checkoutOption',
        'ecommerce': {
          'checkout_option': {
            'actionField': {'step': step, 'option': option}
          }
        }
      });
    }
  }

  private static formatProduct(product: object, qty: number = null): object {
    if (!product || (!product.hasOwnProperty('sku') && !product.hasOwnProperty('extension_attributes') && !product['extension_attributes'].hasOwnProperty('sku'))) {
      return null;
    }

    let productObj = {
      'item_id': product['sku'] || product['extension_attributes']['sku'],
      'id': product['sku'] || product['extension_attributes']['sku'],
      'item_name': product['name'],
      'item_brand': product['brand'] || null,
      'item_variant': product['sku'] || product['extension_attributes']['sku'],
      'price': product['final_price_incl_tax'] || product['price_incl_tax'],
      'category': product.hasOwnProperty('category_names') ? product['category_names'].join(' / ') : '',
      'google_business_vertical': "retail",
    };

    if (qty !== null) {
      productObj['quantity'] = qty;
    }

    return productObj;
  }

  private static formatUser(includeDetails: boolean = false, includeAddress: boolean = false, includeClv: boolean = false, customAddressData: object = null): object {
    let user = appSessionStorage.getItem('checkout-customer', false);
    let userObj = {};
    let clonedAddressData = {};

    if (customAddressData) {
      clonedAddressData = JSON.parse(JSON.stringify(customAddressData));
    }

    if (clonedAddressData && clonedAddressData.hasOwnProperty('street') && Array.isArray(clonedAddressData['street'])) {
      clonedAddressData['street'] = clonedAddressData['street'].join(' ').replace("\n", ' ').trim();
    }

    if (user !== null && user.hasOwnProperty('entity_id')) {
      userObj['user_id'] = 'user-' + user['entity_id'];

      if (includeDetails) {
        userObj['email'] = user['email'] || (clonedAddressData['email'] ?? '');
        userObj['first_name'] = user['firstname'] || (clonedAddressData['firstname'] ?? '');
        userObj['last_name'] = user['lastname'] || (clonedAddressData['lastname'] ?? '');

        if (includeAddress) {
          let address = {};

          if (user.hasOwnProperty('default_billing_address') && user['default_billing_address'] !== null) {
            address = user['default_billing_address'];
          }

          if (user.hasOwnProperty('default_shipping_address') && user['default_shipping_address'] !== null) {
            address = user['default_shipping_address'];
          }

          if (user.hasOwnProperty('addresses') && user['addresses'].length) {
            address = user['addresses'][0];
          }

          if (clonedAddressData) {
            address = clonedAddressData;
          }

          if (address) {
            userObj['street'] = address['street'] || '';
            userObj['city'] = address['city'] || '';
            userObj['region'] = address['region'] || '';
            userObj['postal_code'] = address['postcode'] || '';
            userObj['country'] = address['country_id'] || '';
          }
        }

        if (includeClv) {
          userObj['clv'] = user['clv'] || 0;
        }
      }
    } else if (clonedAddressData !== null) {
      if (includeDetails) {
        userObj['email'] = (user !== null && user.hasOwnProperty('email')) ? user['email'] : '';
        userObj['first_name'] = clonedAddressData['firstname'] || '';
        userObj['last_name'] = clonedAddressData['lastname'] || '';

        if (includeAddress) {
          userObj['street'] = clonedAddressData['street'] || '';
          userObj['city'] = clonedAddressData['city'] || '';
          userObj['region'] = clonedAddressData['region'] || '';
          userObj['postal_code'] = clonedAddressData['postcode'] || '';
          userObj['country'] = clonedAddressData['country_id'] || '';
        }
      }
    }

    return userObj;
  }
}

const enhancedEcommerce = new EnhancedEcommerce();

export {enhancedEcommerce};
